// WHAT IS A "DOUBLE"?
// When the last positive bid was made by an opponent, either player may make
// the call of double. If this is immediately followed by three passes, the
// contract is played doubled. The number of tricks required for the contract
// is not affected, but if the contract is defeated the defending side will score
// additional points. Conversely, if the contract is made, the declaring side
// will score additional points.
// Scoring
// If either side makes a further bid, the earlier double is cancelled. It is
// is possible for this new bid to be doubled.
// A double cannot be followed by another double (by either side), unless there
// is an intervening bid. After a double, it is possible for the declaring side
// to redouble, in which case even more points are at stake if this is the final contract.

const Bid = require('./models/Bid');

const POSSIBLE_CONTRACT_BIDS = require('./POSSIBLE_CONTRACT_BIDS');

const BID_ORDER = {
	N: ['N', 'E', 'S', 'W'],
	E: ['E', 'S', 'W', 'N'],
	S: ['S', 'W', 'N', 'E'],
	W: ['W', 'N', 'E', 'S'],
};

const PAIRS = {
	N: 'NS',
	S: 'NS',
	W: 'EW',
	E: 'EW',
};

/**
 * The Auction controls the logic of bidding for a contract
 * @param  {Player} dealer - the player who is designated as dealer always bids first
 * @return {Auction} auction - the Auction instance
 */
class Auction {
	constructor(dealer) {
		this.turn = 0;
		this.dealer = dealer;

		this.bidOrder = BID_ORDER[dealer.name];
		this.bids = [];
	}

	/**
	 * Makes a bid into this Auction
	 * @param  {String} bidStr a string representing the bid - e.g. `1S` or `X`, or `P`, or `7NT`, or `XX`
	 * @param  {Player} player a player instance who is making the bid
	 * @return {Bid} the bid that was created
	 * @throws {Error} It is not this players turn to bid
	 */
	bid(bidStr, player) {
		if (player.name != this.currentBidder) {
			throw new Error(`It is not this players turn to bid`);
		}

		let bid;
		try {
			bid = new Bid(bidStr, player);
		} catch (err) {
			throw err;
		}

		if (!this.legalBid(bid)) {
			throw new Error(`Cannot make this bid ${bid.name}`);
		}

		this.bids.push(bid);
		this.turn += 1;
		return bid;
	}

	/**
	 * Determine if the given bid is allowed based on the auction so far.
	 *
	 * @param {Bid} bid the bid to test
	 * @return {boolean} true if valid bid, false otherwise
	 */
	legalBid(bid) {
		// can pass any time
		// if it's a contract bid, it must be higher than current contract bid
		// if it's a double it can only be played after a contract bid
		// redouble it can only be played after a double

		if (this.isComplete) {
			return false;
		}

		if (bid.isPass) {
			return true;
		}

		// first contract bid is always allowed
		if (bid.isContractBid && this.currentContractBid == null) return true;

		// cannot double/redouble when no contract bid
		if ((bid.isDouble || bid.isReDouble) && this.currentContractBid == null) {
			return false;
		}

		if (bid.isDouble && this.canDouble) {
			return true;
		}

		if (bid.isReDouble && this.canReDouble) {
			return true;
		}
		return bid.bidValue > this.currentContractBid.bidValue;
	}

	/**
	 * Gets the last bid if the auction, that was not a Pass
	 * @return {Bid} the last non-pass bid
	 */
	get lastPositiveBid() {
		// search the bids for the last bid that was not a pass
		let found = this.bids.reduce((curr, bid) => {
			if (!bid.isPass) {
				curr = bid;
			}
			return curr;
		}, null);

		return found;
	}

	get isDoubled() {
		// if the last postive bid was a double (ignoring passes)
		let last = this.lastPositiveBid;
		return last != null ? last.isDouble : false;
	}
	get isReDoubled() {
		let last = this.lastPositiveBid;
		return last != null ? last.isReDouble : false;
		// if the last postive bid was a redouble (ignoring passes)
	}

	/**
	 * If the last bid was a contract bid, then it can be doubled
	 * @return {boolean} true if the last bid was a contract bid
	 */
	get canDouble() {
		if (this.currentContractBid == null) {
			return false;
		}
		// TODO: only if the last contract bid was an opponent
		if (this.currentContractBid.player.pair == PAIRS[this.currentBidder]) {
			return false;
		}

		if (this.isDoubled) {
			return false;
		}

		return this.lastPositiveBid.isContractBid;
	}

	/**
	 * If the last bid was a double, then can be re-doubled
	 * @return {boolean} true if the last bid was a double
	 */
	get canReDouble() {
		if (this.currentContractBid == null) {
			return false;
		}
		if (
			this.isDoubled &&
			this.lastPositiveBid.player.pair == PAIRS[this.currentBidder]
		) {
			return false;
		}

		return this.isDoubled;
	}

	/**
	 * Get the most recent bid
	 * @return {Bid} - the last bid that was made
	 */
	get lastBid() {
		if (this.bids.length == 0) return null;
		return this.bids[this.bids.length - 1];
	}
	/**
	 * Returns the name of the current contract
	 * @return {String} the current contract e.g. 3NT
	 */
	get currentContractBid() {
		// search the bids for the most recent contract bid
		let contrBid = this.bids.reduce((currentValue, bid) => {
			return bid.isContractBid ? bid : currentValue;
		}, null);

		return contrBid;
	}

	/**
	 * When the game is complete, returns the winning bid name
	 * @return {String} the winning contract - e.g. 2NT
	 */
	get contract() {
		return this.isComplete ? this.currentContractBid.name : null;
	}

	/**
	 * When the game is complete, returns the declarer.
	 * @return {Player} The first player of the partnership to have decared that suit that won the contract
	 */
	get declarer() {
		if (!this.isComplete) return null;

		let contractBid = this.currentContractBid;

		let suit = contractBid.suit;

		// first bid of suit for pair
		let declaringBid = this.bids.find((bid) => {
			return bid.suit == suit && bid.player.pair == contractBid.player.pair;
		});

		return declaringBid.player;
	}

	/**
	 * Is complete when there are three passes in a row
	 * @return {boolean} when pass has been played three times
	 */
	get isComplete() {
		// accumulate passes
		return this.hasThreePasses && this.currentContractBid != null;
	}

	/**
	 * If all players pass with no bids, then the game is "thrown in"
	 * @return {boolean} true if there are three passes but no contract bid
	 */
	get isThrowIn() {
		return this.consecutivePassCount() == 4 && this.currentContractBid == null;
	}

	/**
	 * Counts the number passes since the last contract bid
	 * @private
	 * @return {int} [description]
	 */
	consecutivePassCount() {
		// search the bids for the number of most recent passes
		const passes = this.bids.reduce((passCount, bid) => {
			return bid.isPass ? passCount + 1 : 0; // reset to 0 if not a pass
		}, null);

		return passes;
	}

	/**
	 * Searches the bids to check if the most recent three bids were passes
	 * @return {boolean} [description]
	 */
	get hasThreePasses() {
		return 3 == this.consecutivePassCount();
	}

	/**
	 * The list of bids as strings
	 * @return {String[]} the bids that have been played, in string form e.g. ["P", "P", "7NT", "X", "P", "P", "P"]
	 */
	bidsAsList() {
		return this.bids.map((bid) => bid.bid);
	}

	/**
	 * The list of bids as serializable objects
	 * @return {Object[]} the bids that have been played, in serializable object form e.g. [ { "P" }]
	 */

	get asDetailedList() {
		return this.bids.map((bid) => {
			return {
				bid: bid.name,
				type: bid.isPass
					? 'pass'
					: bid.isContractBid
					? 'contract'
					: bid.isDouble
					? 'double'
					: 'redouble',
				value: bid.value,
				suit: bid.suit,
				player: bid.player.name,
			};
		});
	}

	/**
	 * The name of the player who's turn it is to bid
	 * @return {String} the player name, one of `NESW`
	 */
	get currentBidder() {
		//
		return this.bidOrder[this.turn % 4];
	}

	/**
   * Get a serialized list of all possible bids with isValid property
   * @example
   * // where current bid is 1NT
      {
        contract: [
          {
            bid: '1C',
            value: '1',
            suit: 'C',
            type: 'contract',
            isValid: false
          },
          {
            bid: '1D',
            value: '1',
            suit: 'D',
            type: 'contract',
            isValid: false
          },
          ...
          {
            bid: '7NT',
            value: '7',
            suit: 'NT',
            type: 'contract',
            isValid: true
          }
        ],
        canPass: false,
        canDouble: false,
        canReDouble: false
      }
   * @return {Object} an object containtin a list of possible contract bids
   */
	get serializeValidBidList() {
		let contract = [];
		for (let bid of POSSIBLE_CONTRACT_BIDS) {
			contract.push({
				bid: bid.name,
				value: bid.value,
				suit: bid.suit,
				type: 'contract',
				isValid: this.legalBid(bid),
			});
		}

		return {
			contract,
			canPass: this.isComplete ? false : true,
			canDouble: this.canDouble,
			canReDouble: this.canReDouble,
		};
	}
}

module.exports = Auction;
